import React, { useId } from "react";
import { useStore, useStoreApi } from "reactflow";
import { getBezierLabelGeometry } from "../utils/bezierLabelGeometry.js";
import { arrowStrokeTerminalGap, shortenBezierEndpoint } from "../utils/arrowGeometry.js";
import {
  getAdaptiveEdgeGeometry,
  getFloatingEdgeEndpoints,
  getEdgeTopology,
  getNodeRect,
  getRelevantObstacleRects,
  normalizeEdgeRoutingMode,
} from "../utils/customEdgeGeometry.js";

let cachedNodeRevision = null;
let cachedNodeInternals = null;
let cachedNodeRects = [];

function sharedNodeRects(nodeInternals, nodeRevision) {
  if (cachedNodeRevision === nodeRevision && cachedNodeInternals === nodeInternals) {
    return cachedNodeRects;
  }
  cachedNodeRevision = nodeRevision;
  cachedNodeInternals = nodeInternals;
  cachedNodeRects = Array.from(nodeInternals.entries())
    .map(([nodeId, node]) => ({ nodeId: String(nodeId), rect: getNodeRect(node) }))
    .filter((entry) => Boolean(entry.rect));
  return cachedNodeRects;
}

export default function CustomOffsetEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  label,
  data,
  style = {},
  markerEnd,
}) {
  const generatedMaskId = useId();
  const isReciprocal = Boolean(data?.reciprocal);
  const store = useStoreApi();
  // All edges share one O(1) node-array revision. During a local drag,
  // unrelated obstacle routes are intentionally held until release; React
  // Flow still updates edges attached to the concept being moved.
  const nodeRevision = useStore((state) => data?.deferObstacleRouting ? null : state.nodes);
  const nodeInternals = store.getState().nodeInternals;
  const allEdges = useStore((state) => state.edges);
  const sourceNode = nodeInternals.get(source);
  const targetNode = nodeInternals.get(target);
  const floatingEndpoints = getFloatingEdgeEndpoints({
    sourceNode,
    targetNode,
    sourceId: source,
    targetId: target,
    reciprocal: isReciprocal,
  });
  const edgeSourceX = floatingEndpoints?.sourceX ?? sourceX;
  const edgeSourceY = floatingEndpoints?.sourceY ?? sourceY;
  const edgeTargetX = floatingEndpoints?.targetX ?? targetX;
  const edgeTargetY = floatingEndpoints?.targetY ?? targetY;
  const obstacleRects = getRelevantObstacleRects(sharedNodeRects(nodeInternals, nodeRevision)
    .filter(({ nodeId }) => nodeId !== String(source) && nodeId !== String(target))
    .map(({ rect }) => rect), {
      sourceX: edgeSourceX,
      sourceY: edgeSourceY,
      targetX: edgeTargetX,
      targetY: edgeTargetY,
    });
  const topology = getEdgeTopology({
    edges: allEdges,
    nodeCount: nodeInternals.size,
    sourceId: source,
    targetId: target,
  });
  const {
    controlX1,
    controlY1,
    controlX2,
    controlY2,
    visibleTargetX,
    visibleTargetY,
    points,
  } = getAdaptiveEdgeGeometry({
    sourceX: edgeSourceX,
    sourceY: edgeSourceY,
    targetX: edgeTargetX,
    targetY: edgeTargetY,
    reciprocal: isReciprocal,
    obstacleRects,
    seed: id,
    routingMode: normalizeEdgeRoutingMode(data?.routing),
    topology,
  });

  const edgePoints = [
    { x: edgeSourceX, y: edgeSourceY },
    { x: controlX1, y: controlY1 },
    { x: controlX2, y: controlY2 },
    { x: visibleTargetX, y: visibleTargetY },
  ];
  const strokePoints = shortenBezierEndpoint(
    edgePoints,
    arrowStrokeTerminalGap(style.strokeWidth)
  );
  const edgePath = `M ${edgeSourceX},${edgeSourceY} C ${controlX1},${controlY1} ${controlX2},${controlY2} ${visibleTargetX},${visibleTargetY}`;
  const strokePath = `M ${strokePoints[0].x},${strokePoints[0].y} C ${strokePoints[1].x},${strokePoints[1].y} ${strokePoints[2].x},${strokePoints[2].y} ${strokePoints[3].x},${strokePoints[3].y}`;
  const labelGeometry = getBezierLabelGeometry(points);
  const labelStyle = data?.relationshipLabelStyle || {};
  const haloPadding = Number.isFinite(Number(labelStyle.haloPadding)) ? Number(labelStyle.haloPadding) : 2.5;
  const labelOffset = Number(labelStyle.offset) || 0;
  const labelAngleRadians = (labelGeometry.angle * Math.PI) / 180;
  const labelX = labelGeometry.x - Math.sin(labelAngleRadians) * labelOffset;
  const labelY = labelGeometry.y + Math.cos(labelAngleRadians) * labelOffset;
  const hasLabel = Boolean(label);
  const showLabel = Boolean(hasLabel && data?.showRelationshipLabel !== false);
  const labelTransform = `rotate(${labelGeometry.angle} ${labelX} ${labelY})`;
  const maskId = `edge-label-punchout-${String(id).replace(/[^a-zA-Z0-9_-]/g, "-")}-${generatedMaskId.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
  const maskPadding = 64;
  const maskX = Math.min(edgeSourceX, controlX1, controlX2, visibleTargetX) - maskPadding;
  const maskY = Math.min(edgeSourceY, controlY1, controlY2, visibleTargetY) - maskPadding;
  const maskWidth = Math.max(edgeSourceX, controlX1, controlX2, visibleTargetX) - maskX + maskPadding;
  const maskHeight = Math.max(edgeSourceY, controlY1, controlY2, visibleTargetY) - maskY + maskPadding;
  const labelTypography = {
    fontFamily: "var(--map-font-family)",
    fontSize: Number(labelStyle.fontSize) || 12,
    fontStyle: "normal",
    fontWeight: Number(labelStyle.fontWeight) || 400,
    fontSynthesis: "none",
    letterSpacing: "normal",
  };
  const visiblePathStyle = {
    ...style,
    fill: "none",
    pointerEvents: "none",
    transition: data?.disableOpacityTransition ? "none" : "opacity 160ms ease",
  };
  const canvasVisualOnly = Boolean(data?.canvasVisualOnly);
  const useLabelMask = showLabel && !canvasVisualOnly && !data?.simplifyDuringViewportTransition;

  return (
    <>
      {useLabelMask && (
        <defs aria-hidden="true">
          <mask
            id={maskId}
            x={maskX}
            y={maskY}
            width={maskWidth}
            height={maskHeight}
            maskUnits="userSpaceOnUse"
            maskContentUnits="userSpaceOnUse"
            style={{ maskType: "luminance" }}
          >
            <rect
              x={maskX}
              y={maskY}
              width={maskWidth}
              height={maskHeight}
              fill="white"
            />
            <text
              data-knowledge-edge-mask-label={String(id)}
              x={labelX}
              y={labelY}
              transform={labelTransform}
              textAnchor="middle"
              dominantBaseline="central"
              fill={showLabel ? "black" : "white"}
              stroke={showLabel ? "black" : "white"}
              strokeWidth={haloPadding * 2}
              strokeLinejoin="round"
              paintOrder="stroke"
              style={labelTypography}
            >
              {label}
            </text>
          </mask>
        </defs>
      )}
      <path
        className="react-flow__edge-interaction"
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={18}
        pointerEvents={data?.disablePointerInteraction ? "none" : "stroke"}
      />
      {!canvasVisualOnly && (
        <path
          data-knowledge-edge-path={String(id)}
          id={id}
          d={strokePath}
          fill="none"
          shapeRendering="geometricPrecision"
          style={visiblePathStyle}
          className="react-flow__edge-path"
          mask={useLabelMask ? `url(#${maskId})` : undefined}
        />
      )}
      {!canvasVisualOnly && (
        <path
          data-knowledge-edge-arrow-carrier="true"
          data-knowledge-edge-arrow-carrier-for={String(id)}
          d={edgePath}
          fill="none"
          stroke="transparent"
          strokeWidth={style.strokeWidth || 4.5}
          opacity="1"
          pointerEvents="none"
          markerEnd={markerEnd}
          aria-hidden="true"
        />
      )}
      {hasLabel && (
        <text
          className="knowledge-map-edge__label"
          data-knowledge-edge-label={String(id)}
          x={labelX}
          y={labelY}
          transform={labelTransform}
          textAnchor="middle"
          dominantBaseline="central"
          aria-hidden="true"
          style={{
            ...labelTypography,
            pointerEvents: "none",
            userSelect: "none",
            fill: style.stroke || "#000",
            stroke: "var(--map-label-halo, transparent)",
            strokeWidth: haloPadding * 2,
            strokeOpacity: Number(labelStyle.haloOpacity) || 0,
            strokeLinejoin: "round",
            paintOrder: "stroke",
            opacity: showLabel ? (style.opacity ?? 1) * (Number(labelStyle.opacity) || 1) : 0,
            transition: data?.disableOpacityTransition ? "none" : "opacity 160ms ease",
          }}
        >
          {label}
        </text>
      )}
    </>
  );
}
